import { inject, Injectable, Provider } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  CustomerGetQueryResult,
  IUserInformationGetQuery,
  IUserInformationGetQueryResult,
  UserCustomerListGetQueryResult, UserDeleteCommandResult, UserFavoritesInitializeCommandResult, UserGetQueryResult, UserInformationSetCommand, UserInformationSetCommandResult, UserListGetQueryResult,
  UserPasswordResetCommand, UserPasswordResetCommandResult, UserPasswordResetLinkSendCommand, UserPasswordResetLinkSendCommandResult,
  UserPasswordResetLinkVerifyCommand, UserPasswordResetLinkVerifyCommandResult, UserPasswordSetCommand, UserPasswordSetCommandResult,
  UserRegisterCommand, UserRegisterCommandResult, UserSetCommand, UserSetCommandResult
} from '../../INTEGRA_Shop_Client';
import { IUserService, IUserServiceToken } from './user-service.interface';
import { IUserApiService, IUserApiServiceToken } from '../../api/user/user-api-service.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService implements IUserService {
  private readonly userApiService: IUserApiService;

  public constructor() {
    this.userApiService = inject(IUserApiServiceToken);
  }

  public userLogin(customerNumber: string, coUserNumberOrEmailOrAlias: string, password: string): Observable<string> {
    return this.userApiService.userLogin(customerNumber, coUserNumberOrEmailOrAlias, password);
  }

  public userIDPLogin(userData: any, accessToken: string, idToken: string): Observable<string> {
    return this.userApiService.userIDPLogin(userData, accessToken, idToken);
  }

  public userLogout(): Observable<void> {
    return this.userApiService.userLogout();
  }

  public loginAsMitben(key: string): Observable<string> {
    return this.userApiService.loginAsMitben(key);
  }

  public userLoginByRefreshToken(): Observable<string> {
    return this.userApiService.userLoginByRefreshToken();
  }

  public userInformationGet(
    params?: Partial<IUserInformationGetQuery>,
    allowCancellation: boolean = false
  ): Observable<IUserInformationGetQueryResult> {
    return this.userApiService.userInformationGet(params, allowCancellation).pipe(
      map(
        (result: IUserInformationGetQueryResult) => {
          return result;
        }
      )
    );
  }

  public userInformationSet(command: UserInformationSetCommand): Observable<UserInformationSetCommandResult> {
    return this.userApiService.userInformationSet(command);
  }

  public userGet(userNumber?: string | undefined, loadOnlyActive?: boolean | undefined): Observable<UserGetQueryResult> {
    if (loadOnlyActive == undefined) {
      loadOnlyActive = true;
    }
    return this.userApiService.userGet(userNumber, loadOnlyActive);
  }

  public userSet(command: UserSetCommand): Observable<UserSetCommandResult> {
    return this.userApiService.userSet(command);
  }

  public userListGet(): Observable<UserListGetQueryResult> {
    return this.userApiService.userListGet();
  }

  public userDelete(userNumber?: string | undefined): Observable<UserDeleteCommandResult> {
    return this.userApiService.userDelete(userNumber);
  }

  public userPasswordReset(command: UserPasswordResetCommand): Observable<UserPasswordResetCommandResult> {
    return this.userApiService.userPasswordReset(command);
  }

  public userPasswordResetLinkSend(command: UserPasswordResetLinkSendCommand): Observable<UserPasswordResetLinkSendCommandResult> {
    return this.userApiService.userPasswordResetLinkSend(command);
  }

  public userPasswordResetLinkVerify(command: UserPasswordResetLinkVerifyCommand): Observable<UserPasswordResetLinkVerifyCommandResult> {
    return this.userApiService.userPasswordResetLinkVerify(command);
  }

  public userPasswordSet(command: UserPasswordSetCommand): Observable<UserPasswordSetCommandResult> {
    return this.userApiService.userPasswordSet(command);
  }

  public customerGet(customerNumber: string | undefined): Observable<CustomerGetQueryResult> {
    return this.userApiService.customerGet(customerNumber);
  }

  public userCustomerListGet(): Observable<UserCustomerListGetQueryResult> {
    return this.userApiService.userCustomerListGet();
  }

  public userSwitchCustomer(customerNumber?: string | undefined): Observable<string> {
    return this.userApiService.userSwitchCustomer(customerNumber);
  }

  public userRegister(command: UserRegisterCommand): Observable<UserRegisterCommandResult> {
    return this.userApiService.userRegister(command);
  }

  public userFavoritesInitialize(): Observable<UserFavoritesInitializeCommandResult> {
    return this.userApiService.userFavoritesInitialize();
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const UserServiceProvider: Provider[] = [
  { provide: IUserServiceToken, useExisting: UserService }
];
